import {
  degToRad,
  ensureCounterClockwise,
  localToWorld,
  pointOnPolygonBoundary,
  subtractPolygons,
  triangulatePolygon,
  triangulateRegion,
  type Region,
  type Vec2,
  type Vec3,
} from '@indusketch/geometry';
import { clampRoofPitch, storeyCorners } from '../building.js';
import type { Building } from '../types.js';

/** Flat slab thickness used purely for visual separation from the walls. */
export const FLAT_ROOF_THICKNESS = 0.15;

export interface RoofMesh {
  /** Triangle soup in model coordinates, three vertices per triangle. */
  readonly triangles: readonly Vec3[];
  readonly ridgeHeight: number;
}

/**
 * Simplified roof envelope for visualization only (no construction detail). For gable and
 * mono-pitch roofs the ridge runs along the longer side, as is typical for halls.
 */
export function roofMesh(b: Building): RoofMesh {
  if (b.footprint) {
    const roof = outlineRoof(b, b.footprint);
    const pts = roof.points;
    return {
      triangles: triangulatePolygon(b.footprint).flatMap(
        ([i, j, k]) => [pts[i], pts[j], pts[k]] as Vec3[],
      ),
      ridgeHeight: roof.top,
    };
  }
  const alongX = b.width >= b.depth;
  const a = (alongX ? b.width : b.depth) / 2;
  const s = (alongX ? b.depth : b.width) / 2;
  const e = b.eaveHeight;
  const tan = Math.tan(degToRad(b.roofPitchDeg));
  const placement = { position: b.center, rotation: b.rotation };

  // Canonical frame: u along the ridge, v across the span.
  const p = (u: number, v: number, z: number): Vec3 => {
    const local = alongX ? { x: u, y: v } : { x: v, y: u };
    const w = localToWorld(local, placement);
    return { x: w.x, y: w.y, z };
  };
  const quad = (q: [Vec3, Vec3, Vec3, Vec3]): Vec3[] => [q[0], q[1], q[2], q[0], q[2], q[3]];

  if (b.roofType === 'flat' || b.roofPitchDeg === 0) {
    const top = e + FLAT_ROOF_THICKNESS;
    return {
      triangles: quad([p(-a, -s, top), p(a, -s, top), p(a, s, top), p(-a, s, top)]),
      ridgeHeight: top,
    };
  }

  if (b.roofType === 'mono') {
    const h = e + 2 * s * tan;
    return {
      triangles: [
        ...quad([p(-a, -s, e), p(a, -s, e), p(a, s, h), p(-a, s, h)]),
        p(-a, -s, e),
        p(-a, s, e),
        p(-a, s, h),
        p(a, -s, e),
        p(a, s, h),
        p(a, s, e),
      ],
      ridgeHeight: h,
    };
  }

  const r = e + s * tan;
  return {
    triangles: [
      ...quad([p(-a, -s, e), p(a, -s, e), p(a, 0, r), p(-a, 0, r)]),
      ...quad([p(a, s, e), p(-a, s, e), p(-a, 0, r), p(a, 0, r)]),
      // Gable infill above the end walls.
      p(-a, s, e),
      p(-a, -s, e),
      p(-a, 0, r),
      p(a, -s, e),
      p(a, s, e),
      p(a, 0, r),
    ],
    ridgeHeight: r,
  };
}

export function ridgeHeight(b: Building): number {
  return roofMesh(b).ridgeHeight;
}

/** Pitch (degrees) that puts the ridge at `ridge`, kept within the usual limits. */
export function pitchForRidge(b: Building, ridge: number): number {
  const probe = 10;
  // Horizontal run from the eaves to the ridge, whatever the roof type and outline.
  const run =
    (ridgeHeight({ ...b, roofPitchDeg: probe }) - b.eaveHeight) / Math.tan(degToRad(probe));
  if (run <= 0) return b.roofPitchDeg;
  const deg = (Math.atan(Math.max(0, ridge - b.eaveHeight) / run) * 180) / Math.PI;
  return clampRoofPitch(b.roofType, deg);
}

/** Rectangle in the texture coordinates (metres) of a roof plane. */
export interface UvRect {
  readonly u0: number;
  readonly v0: number;
  readonly u1: number;
  readonly v1: number;
}

/** Roof face with texture coordinates in metres: u along the ridge, v down the slope. */
export interface RoofPlane {
  /** Four corners for a rectangular hall; any number for a drawn outline (see `triangles`). */
  readonly corners: readonly Vec3[];
  readonly uv: readonly Vec2[];
  /** Openings cut into this face (skylights, smoke vents), clipped to the face. */
  readonly holes: readonly UvRect[];
  /** Index triples into `corners` for faces with more than four corners. */
  readonly triangles?: readonly number[];
}

/**
 * Roof over a drawn outline: flat, or a mono-pitch plane rising at right angles to the
 * chosen eave edge (starting from the outline point furthest out on the eave side).
 */
function outlineRoof(b: Building, outline: readonly Vec2[]) {
  const e = b.eaveHeight;
  const n = outline.length;
  if (b.roofType !== 'mono' || b.roofPitchDeg === 0) {
    const top = e + FLAT_ROOF_THICKNESS;
    const uvOf = (p: Vec2): Vec2 => ({ x: p.x, y: p.y });
    return {
      flat: true,
      top,
      points: outline.map((p) => ({ x: p.x, y: p.y, z: top })),
      uv: outline.map(uvOf),
      height: () => top,
      uvOf,
    };
  }
  const k = Math.min(b.eaveEdge ?? 0, n - 1);
  const a = outline[k] as Vec2;
  const c = outline[(k + 1) % n] as Vec2;
  const len = Math.hypot(c.x - a.x, c.y - a.y) || 1;
  const along = { x: (c.x - a.x) / len, y: (c.y - a.y) / len };
  // Counter-clockwise outline: the inside lies to the left of each edge.
  const inward = { x: -along.y, y: along.x };
  const depthOf = (p: Vec2) => (p.x - a.x) * inward.x + (p.y - a.y) * inward.y;
  const low = Math.min(...outline.map(depthOf));
  const tan = Math.tan(degToRad(b.roofPitchDeg));
  const height = (p: Vec2) => e + (depthOf(p) - low) * tan;
  const slope = Math.sqrt(1 + tan * tan);
  const high = Math.max(...outline.map(depthOf));
  // u along the eave, v down the slope (from the high side), both in metres.
  const uvOf = (p: Vec2): Vec2 => ({
    x: (p.x - a.x) * along.x + (p.y - a.y) * along.y,
    y: (high - depthOf(p)) * slope,
  });
  return {
    flat: false,
    top: e + (high - low) * tan,
    points: outline.map((p) => ({ x: p.x, y: p.y, z: height(p) })),
    uv: outline.map(uvOf),
    height,
    uvOf,
  };
}

function outlineRoofParts(
  b: Building,
  outline: readonly Vec2[],
  openings: readonly RoofOpening[],
): RoofParts {
  const roof = outlineRoof(b, outline);
  const n = outline.length;
  const e = b.eaveHeight;
  const gables: GableTriangle[] = [];
  const edges: RoofEdge[] = [];
  outline.forEach((p, i) => {
    const q = outline[(i + 1) % n] as Vec2;
    const len = Math.hypot(q.x - p.x, q.y - p.y) || 1;
    const outward = { x: (q.y - p.y) / len, y: -(q.x - p.x) / len };
    const top = roof.points;
    const pa = top[i] as Vec3;
    const pb = top[(i + 1) % n] as Vec3;
    if (roof.flat) {
      edges.push({ kind: 'parapet', a: pa, b: pb, outward });
      return;
    }
    // Wall area above the eaves up to the sloping roof.
    const lowA = { x: p.x, y: p.y, z: e };
    const lowB = { x: q.x, y: q.y, z: e };
    const uv = (u: number, z: number): Vec2 => ({ x: u, y: z });
    if (pa.z - e > 0.01 && pb.z - e > 0.01) {
      gables.push({ corners: [lowA, lowB, pb], uv: [uv(0, e), uv(len, e), uv(len, pb.z)] });
      gables.push({ corners: [lowA, pb, pa], uv: [uv(0, e), uv(len, pb.z), uv(0, pa.z)] });
    } else if (pb.z - e > 0.01) {
      gables.push({ corners: [lowA, lowB, pb], uv: [uv(0, e), uv(len, e), uv(len, pb.z)] });
    } else if (pa.z - e > 0.01) {
      gables.push({ corners: [lowA, lowB, pa], uv: [uv(0, e), uv(len, e), uv(0, pa.z)] });
    }
    const level = Math.abs(pa.z - pb.z) < 0.01;
    const kind: RoofEdgeKind = level ? (pa.z - e < 0.01 ? 'eave' : 'top') : 'verge';
    edges.push({ kind, a: pa, b: pb, outward });
  });
  return {
    flat: roof.flat,
    planes:
      openings.length > 0
        ? cutRoofPlanes(b, outline, openings, roof.height, roof.uvOf)
        : [
            {
              corners: roof.points,
              uv: roof.uv,
              holes: [],
              triangles: triangulatePolygon(outline).flat(),
            },
          ],
    gables,
    edges,
  };
}
/** A free-standing office with storeys smaller than the one below (Staffelgeschoss). */
const hasSetbacks = (b: Building): boolean => {
  const o = b.office;
  return o?.placement === 'outdoor' && o.setbacks.some((s) => s.level < o.floors.length);
};

/**
 * Flat roofs of an office with setbacks: over the top storey at the building's top, and on
 * every storey below where the one above is smaller (unless that area is a roof terrace).
 * Parapets run along the outer edges; next to the storey above its wall closes the roof.
 */
function setbackRoofParts(b: Building, openings: readonly RoofOpening[]): RoofParts {
  const o = b.office as NonNullable<Building['office']>;
  const n = o.floors.length;
  const e = [0];
  for (const f of o.floors) e.push((e[e.length - 1] ?? 0) + f.clearHeight + o.slabThickness);
  const planes: RoofPlane[] = [];
  const edges: RoofEdge[] = [];
  const addRoof = (regions: readonly Region[], z: number, outline: readonly Vec2[] | null) => {
    const at = (p: Vec2): Vec3 => ({ x: p.x, y: p.y, z });
    for (const r of regions) {
      const rings = [r.outer, ...r.holes.map((h) => ensureCounterClockwise(h).reverse())];
      for (const ring of rings)
        ring.forEach((p, i) => {
          const q = ring[(i + 1) % ring.length] as Vec2;
          const len = Math.hypot(q.x - p.x, q.y - p.y);
          if (len < 1e-6) return;
          const mid = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
          if (outline && !pointOnPolygonBoundary(mid, outline, 0.05)) return;
          // Outer rings run counter-clockwise, holes clockwise: outward is to the right.
          edges.push({
            kind: 'parapet',
            a: at(p),
            b: at(q),
            outward: { x: (q.y - p.y) / len, y: -(q.x - p.x) / len },
          });
        });
      const { points, triangles } = triangulateRegion(r);
      planes.push({
        corners: points.map(at),
        uv: points.map((p) => ({ x: p.x, y: p.y })),
        holes: [],
        triangles,
      });
    }
  };
  const top = storeyCorners(b, n - 1);
  // Roof elements sit on the top roof, cut out of it; parapets only along its outline.
  addRoof(
    openings.length > 0
      ? subtractPolygons(
          top,
          openings.map((o) => roofOpeningPolygon(b, o)),
        )
      : [{ outer: top, holes: [] }],
    (e[n] ?? b.eaveHeight) + FLAT_ROOF_THICKNESS,
    openings.length > 0 ? top : null,
  );
  for (const s of o.setbacks) {
    if (s.level >= n || s.terrace) continue;
    const below = storeyCorners(b, s.level - 1);
    const regions = subtractPolygons(below, [storeyCorners(b, s.level)]);
    addRoof(regions, (e[s.level] ?? 0) + FLAT_ROOF_THICKNESS, below);
  }
  return { flat: true, planes, gables: [], edges };
}

/** Opening through the roof, as a rectangle in the building's local frame (x = width). */
export interface RoofOpening {
  readonly center: Vec2;
  readonly width: number;
  readonly depth: number;
  /** Turn against the building's axes (radians); missing means axis-parallel. */
  readonly rotation?: number;
}

/** Corners of an opening in world coordinates (counter-clockwise). */
export function roofOpeningPolygon(b: Building, o: RoofOpening): Vec2[] {
  const own = { position: o.center, rotation: o.rotation ?? 0 };
  const placement = { position: b.center, rotation: b.rotation };
  const hw = o.width / 2;
  const hd = o.depth / 2;
  return [
    { x: -hw, y: -hd },
    { x: hw, y: -hd },
    { x: hw, y: hd },
    { x: -hw, y: hd },
  ].map((p) => localToWorld(localToWorld(p, own), placement));
}

/**
 * Roof faces over `outline` with the openings cut out, as simple polygons (holes joined by a
 * bridge) at the height and with the texture coordinates of the roof plane.
 */
function cutRoofPlanes(
  b: Building,
  outline: readonly Vec2[],
  openings: readonly RoofOpening[],
  height: (p: Vec2) => number,
  uvOf: (p: Vec2) => Vec2,
): RoofPlane[] {
  const regions = subtractPolygons(
    outline,
    openings.map((o) => roofOpeningPolygon(b, o)),
  );
  return regions.map((r) => {
    const { points, triangles } = triangulateRegion(r);
    return {
      corners: points.map((p) => ({ x: p.x, y: p.y, z: height(p) })),
      uv: points.map(uvOf),
      holes: [],
      triangles,
    };
  });
}

/**
 * Rectangles covering a roof plane except its holes, row by row between the hole edges; the
 * holes may touch the plane edge or each other.
 */
export function roofPlaneCells(plane: RoofPlane): UvRect[] {
  const uMax = Math.max(...plane.uv.map((t) => t.x));
  const vMax = Math.max(...plane.uv.map((t) => t.y));
  if (plane.holes.length === 0) return [{ u0: 0, v0: 0, u1: uMax, v1: vMax }];
  const vs = [...new Set([0, vMax, ...plane.holes.flatMap((h) => [h.v0, h.v1])])].sort(
    (x, y) => x - y,
  );
  const cells: UvRect[] = [];
  for (let i = 0; i + 1 < vs.length; i++) {
    const v0 = vs[i] ?? 0;
    const v1 = vs[i + 1] ?? 0;
    if (v1 - v0 < 1e-6) continue;
    const mid = (v0 + v1) / 2;
    const blocked = plane.holes.filter((h) => h.v0 < mid && mid < h.v1).sort((x, y) => x.u0 - y.u0);
    let u = 0;
    for (const h of blocked) {
      if (h.u0 - u > 1e-6) cells.push({ u0: u, v0, u1: h.u0, v1 });
      u = Math.max(u, h.u1);
    }
    if (uMax - u > 1e-6) cells.push({ u0: u, v0, u1: uMax, v1 });
  }
  return cells;
}

/** Wall area above the eaves (gable or side of a mono-pitch roof): u along the wall, v height. */
export interface GableTriangle {
  readonly corners: readonly [Vec3, Vec3, Vec3];
  readonly uv: readonly [Vec2, Vec2, Vec2];
}

/**
 * Trim along a roof edge: `eave` gutter, `verge` flashing on a sloped edge, `ridge` cap,
 * `top` flashing along the high side of a mono-pitch roof, `parapet` of a flat roof.
 */
export type RoofEdgeKind = 'eave' | 'verge' | 'ridge' | 'top' | 'parapet';

export interface RoofEdge {
  readonly kind: RoofEdgeKind;
  readonly a: Vec3;
  readonly b: Vec3;
  /** Horizontal direction pointing away from the building (unit vector). */
  readonly outward: Vec2;
}

export interface RoofParts {
  readonly flat: boolean;
  readonly planes: readonly RoofPlane[];
  readonly gables: readonly GableTriangle[];
  readonly edges: readonly RoofEdge[];
}

/**
 * Roof split into faces, the wall areas above the eaves and the edges, for a detailed 3D view
 * (cladding textures, gutters, verges, parapets). Same frame and heights as `roofMesh`.
 */
export function roofParts(b: Building, openings: readonly RoofOpening[] = []): RoofParts {
  if (hasSetbacks(b)) return setbackRoofParts(b, openings);
  if (b.footprint) return outlineRoofParts(b, b.footprint, openings);
  const alongX = b.width >= b.depth;
  const a = (alongX ? b.width : b.depth) / 2;
  const s = (alongX ? b.depth : b.width) / 2;
  const e = b.eaveHeight;
  const tan = Math.tan(degToRad(b.roofPitchDeg));
  const placement = { position: b.center, rotation: b.rotation };
  const p = (u: number, v: number, z: number): Vec3 => {
    const w = localToWorld(alongX ? { x: u, y: v } : { x: v, y: u }, placement);
    return { x: w.x, y: w.y, z };
  };
  const origin = localToWorld({ x: 0, y: 0 }, placement);
  const dir = (du: number, dv: number): Vec2 => {
    const w = localToWorld(alongX ? { x: du, y: dv } : { x: dv, y: du }, placement);
    return { x: w.x - origin.x, y: w.y - origin.y };
  };
  const uv = (u: number, v: number): Vec2 => ({ x: u, y: v });
  /** Opening in the canonical frame: centre and half sizes along (u) and across (v) the ridge. */
  const canonical = (o: RoofOpening) => ({
    cu: alongX ? o.center.x : o.center.y,
    cv: alongX ? o.center.y : o.center.x,
    hu: (alongX ? o.width : o.depth) / 2,
    hv: (alongX ? o.depth : o.width) / 2,
  });
  // Texture holes are axis-parallel; turned openings only go into a flat roof (see below).
  const square = openings.filter((o) => o.rotation === undefined);
  /** Openings within the span band [vMin, vMax], mapped to the plane's texture coordinates. */
  const holes = (vMin: number, vMax: number, toV: (v: number) => number): UvRect[] =>
    square.flatMap((o) => {
      const { cu, cv, hu, hv } = canonical(o);
      const u0 = Math.max(-a, cu - hu);
      const u1 = Math.min(a, cu + hu);
      const v0 = Math.max(vMin, cv - hv);
      const v1 = Math.min(vMax, cv + hv);
      if (u1 - u0 < 0.01 || v1 - v0 < 0.01) return [];
      const [t0, t1] = [toV(v0), toV(v1)].sort((x, y) => x - y) as [number, number];
      return [{ u0: u0 + a, v0: t0, u1: u1 + a, v1: t1 }];
    });

  if (b.roofType === 'flat' || b.roofPitchDeg === 0) {
    const top = e + FLAT_ROOF_THICKNESS;
    const c = [p(-a, -s, top), p(a, -s, top), p(a, s, top), p(-a, s, top)] as const;
    // Turned openings are cut out of the roof polygon; axis-parallel ones as texture holes.
    const turned = openings.some((o) => o.rotation !== undefined);
    return {
      flat: true,
      planes: turned
        ? cutRoofPlanes(
            b,
            c.map((q) => ({ x: q.x, y: q.y })),
            openings,
            () => top,
            (q) => ({ x: q.x, y: q.y }),
          )
        : [
            {
              corners: c,
              uv: [uv(0, 0), uv(2 * a, 0), uv(2 * a, 2 * s), uv(0, 2 * s)],
              holes: holes(-s, s, (v) => v + s),
            },
          ],
      gables: [],
      edges: [
        { kind: 'parapet', a: c[0], b: c[1], outward: dir(0, -1) },
        { kind: 'parapet', a: c[1], b: c[2], outward: dir(1, 0) },
        { kind: 'parapet', a: c[2], b: c[3], outward: dir(0, 1) },
        { kind: 'parapet', a: c[3], b: c[0], outward: dir(-1, 0) },
      ],
    };
  }

  if (b.roofType === 'mono') {
    const h = e + 2 * s * tan;
    const slope = Math.hypot(2 * s, h - e);
    const side = (u: number): GableTriangle => ({
      corners: [p(u, -s, e), p(u, s, e), p(u, s, h)],
      uv: [uv(0, e), uv(2 * s, e), uv(2 * s, h)],
    });
    return {
      flat: false,
      planes: [
        {
          corners: [p(-a, -s, e), p(a, -s, e), p(a, s, h), p(-a, s, h)],
          uv: [uv(0, slope), uv(2 * a, slope), uv(2 * a, 0), uv(0, 0)],
          holes: holes(-s, s, (v) => ((s - v) * slope) / (2 * s)),
        },
      ],
      gables: [side(-a), side(a)],
      edges: [
        { kind: 'eave', a: p(-a, -s, e), b: p(a, -s, e), outward: dir(0, -1) },
        { kind: 'top', a: p(-a, s, h), b: p(a, s, h), outward: dir(0, 1) },
        { kind: 'verge', a: p(-a, -s, e), b: p(-a, s, h), outward: dir(-1, 0) },
        { kind: 'verge', a: p(a, -s, e), b: p(a, s, h), outward: dir(1, 0) },
      ],
    };
  }

  const r = e + s * tan;
  const slope = Math.hypot(s, r - e);
  // The ridge cap stops where a ridge skylight crosses it.
  const gaps = square
    .map(canonical)
    .filter((o) => Math.abs(o.cv) < o.hv)
    .map((o) => [o.cu - o.hu, o.cu + o.hu] as const)
    .sort((x, y) => x[0] - y[0]);
  const ridges: RoofEdge[] = [];
  let from = -a;
  for (const [g0, g1] of gaps) {
    if (g0 - from > 0.01)
      ridges.push({
        kind: 'ridge',
        a: p(from, 0, r),
        b: p(Math.min(g0, a), 0, r),
        outward: dir(0, 1),
      });
    from = Math.max(from, g1);
  }
  if (a - from > 0.01)
    ridges.push({ kind: 'ridge', a: p(from, 0, r), b: p(a, 0, r), outward: dir(0, 1) });
  const gable = (u: number): GableTriangle => ({
    corners: [p(u, -s, e), p(u, s, e), p(u, 0, r)],
    uv: [uv(0, e), uv(2 * s, e), uv(s, r)],
  });
  return {
    flat: false,
    planes: [
      {
        corners: [p(-a, -s, e), p(a, -s, e), p(a, 0, r), p(-a, 0, r)],
        uv: [uv(0, slope), uv(2 * a, slope), uv(2 * a, 0), uv(0, 0)],
        holes: holes(-s, 0, (v) => (-v * slope) / s),
      },
      {
        corners: [p(a, s, e), p(-a, s, e), p(-a, 0, r), p(a, 0, r)],
        uv: [uv(2 * a, slope), uv(0, slope), uv(0, 0), uv(2 * a, 0)],
        holes: holes(0, s, (v) => (v * slope) / s),
      },
    ],
    gables: [gable(-a), gable(a)],
    edges: [
      { kind: 'eave', a: p(-a, -s, e), b: p(a, -s, e), outward: dir(0, -1) },
      { kind: 'eave', a: p(a, s, e), b: p(-a, s, e), outward: dir(0, 1) },
      { kind: 'verge', a: p(-a, -s, e), b: p(-a, 0, r), outward: dir(-1, 0) },
      { kind: 'verge', a: p(-a, 0, r), b: p(-a, s, e), outward: dir(-1, 0) },
      { kind: 'verge', a: p(a, -s, e), b: p(a, 0, r), outward: dir(1, 0) },
      { kind: 'verge', a: p(a, 0, r), b: p(a, s, e), outward: dir(1, 0) },
      ...ridges,
    ],
  };
}
